import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import notify from "./ui/notify";

// Bidder watchlist as a small shared hook. Each page that needs it calls
// useWatchlist() and gets: has(carId), toggle(carId), the raw rows, and whether
// the viewer is signed in. Toggling is optimistic and reverts on failure.
export default function useWatchlist() {
    const [ids, setIds] = useState(() => new Set());
    const [rows, setRows] = useState([]);
    const [ready, setReady] = useState(false);
    const [authed, setAuthed] = useState(true);
    const busyRef = useRef(new Set());

    const load = useCallback(async () => {
        try {
            const res = await axios.get("/api/auction/watchlist");
            setIds(new Set((res.data.car_ids || []).map(Number)));
            setRows(res.data.watchlist || []);
            setAuthed(true);
        } catch (err) {
            if (err && err.response && err.response.status === 401) setAuthed(false);
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const has = useCallback((carId) => ids.has(Number(carId)), [ids]);

    const toggle = useCallback(async (carId) => {
        const cid = Number(carId);
        if (!cid || busyRef.current.has(cid)) return;
        if (!authed) {
            notify.warning({ en: "Sign in to use your favorites.", geo: "რჩეულებისთვის გაიარეთ ავტორიზაცია.", ru: "Войдите, чтобы использовать избранное.", am: "Մուտք գործեք՝ ընտրանին օգտագործելու համար։" });
            return;
        }
        busyRef.current.add(cid);
        const wasWatched = ids.has(cid);
        setIds((prev) => { const n = new Set(prev); if (wasWatched) n.delete(cid); else n.add(cid); return n; });
        try {
            if (wasWatched) {
                await axios.delete(`/api/auction/watchlist/${cid}`);
                setRows((prev) => prev.filter((r) => Number(r.car_id) !== cid));
            } else {
                await axios.post("/api/auction/watchlist", { car_id: cid });
                load();
            }
        } catch (err) {
            setIds((prev) => { const n = new Set(prev); if (wasWatched) n.add(cid); else n.delete(cid); return n; });
            if (err && err.response && err.response.status === 401) {
                setAuthed(false);
                notify.warning({ en: "Sign in to use your favorites.", geo: "რჩეულებისთვის გაიარეთ ავტორიზაცია.", ru: "Войдите, чтобы использовать избранное.", am: "Մուտք գործեք՝ ընտրանին օգտագործելու համար։" });
            } else {
                notify.apiError(err);
            }
        } finally {
            busyRef.current.delete(cid);
        }
    }, [ids, authed, load]);

    return { ids, rows, has, toggle, ready, authed, reload: load };
}
